<template>
  <component
    :is="!isDisabled && (to || href) ? (to ? RouterLink : 'a') : 'button'"
    ref="tooltipAnchorEl"
    :type="nativeType"
    v-bind="$attrs"
    :to="!isDisabled && to ? to : null"
    :href="!isDisabled && !to && href ? href : null"
    :target="!isDisabled && !to && href ? '_blank' : null"
    :rel="!isDisabled && !to && href ? rel : null"
    class="rf-button group [&:not(.disabled)]:focus-visible:outline-beige-90 relative inline-flex items-center justify-center overflow-hidden rounded-lg [&:not(.disabled)]:focus-visible:outline-2 [&:not(.disabled)]:focus-visible:outline-offset-2"
    :class="{
      [typeClasses[type]]: true,
      [sizeClasses[size]]: !sizelessTypes.includes(props.type),
      disabled: isDisabled,
    }"
    :disabled="isDisabled"
    :aria-busy="props.loading"
    @focus="() => tooltipEl?.show?.()"
    @mouseenter="() => tooltipEl?.show?.()"
    @blur="() => tooltipEl?.hide?.()"
    @mouseleave="() => tooltipEl?.hide?.()"
  >
    <div
      class="rf-button--container inline-flex h-full items-center font-normal"
      :class="
        ['main', 'secondary'].includes(type)
          ? ['x-small', 'small'].includes(size)
            ? 'gap-2'
            : 'gap-4'
          : 'gap-2'
      "
    >
      <RfSpinnerIcon v-if="props.loading" class="h-4 w-4 animate-spin" />
      <template v-else>
        <slot v-if="$slots.default" />
        <template v-else> {{ text }} </template>
      </template>
    </div>
    <div
      class="rf-button--ripple absolute aspect-square h-0 rounded-full transition-all duration-150 ease-in-out group-active:h-[200%]"
    ></div>
    <RfTooltip
      v-if="
        (isDisabled && typeof disabled === 'string' && disabled !== '') ||
        (tooltipMessage && typeof tooltipMessage === 'string' && tooltipMessage !== '')
      "
      ref="tooltipEl"
      :position="tooltipPos"
      :anchor="tooltipAnchorEl"
      :contentClass="tooltipClass"
    >
      <template #tooltip> {{ disabled || tooltipMessage }} </template>
    </RfTooltip>
  </component>
</template>

<script>
const typeClasses = {
  main: "rf-button--main icon-black-1 bg-yellow text-black-1 [&.disabled]:bg-beige-20 [&.disabled]:text-beige-50 [&.disabled]:icon-beige-50",
  secondary:
    "rf-button--secondary bg-beige-50 [&_.rf-button--container]:text-white-1 icon-white-1 [&.disabled]:icon-beige-50 [&.disabled]:text-beige-50 [&.disabled]:bg-beige-5",
  tertiary:
    "rf-button--tertiary border border-solid border-beige-90 bg-none [&:not(.disabled)]:focus-visible:bg-black/20 [&.disabled]:border-beige-90 text-beige-90 [&.disabled]:text-beige-20 enabled-active:bg-strokes",
  quaternary:
    "rf-button--quaternary border border-solid border-beige-20 bg-none [&:not(.disabled)]:focus-visible:bg-black/20 [&.disabled]:border-beige-20 text-beige-50 [&.disabled]:text-beige-20 enabled-active:bg-strokes",
  ghost:
    "rf-button--ghost bg-none [&:not(.disabled)]:focus-visible:bg-black/20 text-beige-50 [&.disabled]:text-beige-20 enabled-active:bg-strokes",
  none: "rf-button--none [&.disabled]:cursor-default!",
  plain:
    "rf-button--none [&.disabled_.rf-icon]:opacity-50 [&.disabled]:opacity-50 [&_.rf-icon]:fill-beige-50 text-black-1",
  link: "rf-button--link text-blue-50 [&_.rf-icon]:fill-blue-50 [&.disabled]:text-beige-50 [&.disabled]:icon-beige-50",
  table: "rf-button--table [&:not(.disabled)]:hover:bg-beige-1",
  icon: "rf-button--icon [&:not(.disabled)]:hover:bg-input-background",
  white: "rf-button--white bg-white-1 text-black-2 icon-beige-50",
  round:
    "rf-button--round rounded-full! p-0 icon-beige-50 text-beige-50 enabled-hover:bg-beige-1 enabled-hover:text-beige-90 enabled-hover:icon-beige-90 enabled-active:bg-beige-5 enabled-active:text-beige-90 enabled-active:icon-beige-90 [&:not(.disabled)]:focus-visible:outline-beige-90 [&:not(.disabled)]:focus-visible:outline-2 [&:not(.disabled)]:focus-visible:outline-offset-2",
  underline:
    "rf-button--underline rounded-none! bg-transparent! border-b border-solid border-beige-40 text-xs text-beige-40 icon-beige-40 [&:not(.disabled)]:hover:border-beige-90 [&:not(.disabled)]:hover:bg-transparent! [&:not(.disabled)]:hover:text-beige-90 [&:not(.disabled)]:hover:icon-beige-90 [&.disabled]:border-beige-20 [&.disabled]:icon-beige-20 [&.disabled]:text-beige-20",
};

const sizeClasses = {
  "xx-small": "rf-button--xxsm text-sm font-normal not-italic",
  "x-small": "rf-button--xsm text-sm font-normal not-italic",
  "small": "rf-button--sm text-sm font-normal not-italic",
  "base": "rf-button--base text-sm font-normal not-italic",
  "large": "rf-button--lg text-base font-normal not-italic",
  "x-large": "rf-button--xlg text-base font-normal not-italic",
  "none": "",
  "link": "rf-button--link text-sm font-normal not-italic",
  "link-sm": "rf-button--link-sm text-sm font-normal not-italic",
};

// Icon-only/text-only types that never want the height/padding/font-size a size class adds.
const sizelessTypes = ["table", "icon", "underline"];
</script>

<script setup>
import { computed, ref, useAttrs } from "vue";
import { RouterLink } from "vue-router";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfSpinnerIcon from "@/components/icons/RfSpinnerIcon.vue";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  text: { type: String, default: "" },
  icon: { type: String, default: null },
  tooltipPos: { type: String, default: null },
  tooltipMessage: { type: String, default: null },
  tooltipClass: { type: [String, Array, Object], default: null },
  to: { type: [Object, String], default: null },
  href: { type: String, default: null },
  disabled: { type: [String, Boolean], default: false },
  loading: { type: Boolean, default: false },
  nativeType: { type: String, default: "button" },
  type: {
    type: String,
    default: "main",
    validator: v => Object.keys(typeClasses).includes(v),
  },
  size: {
    type: String,
    default: "base",
    validator: v => Object.keys(sizeClasses).includes(v),
  },
});

const attrs = useAttrs();
const tooltipEl = ref();
const tooltipAnchorEl = ref();

const rel = computed(() => {
  if (!props.href) return null;
  const required = ["noopener", "noreferrer"];
  const caller = attrs.rel ? String(attrs.rel).split(/\s+/).filter(Boolean) : [];
  return [...new Set([...caller, ...required])].join(" ");
});

const isDisabled = computed(
  () =>
    props.loading ||
    (props.disabled !== false && props.disabled !== null && props.disabled !== undefined),
);
</script>

<style lang="scss" scoped>
.rf-button {
  // Icon-only types share the same padding instead of each repeating `p-2` in typeClasses.
  &--table,
  &--icon,
  &--white {
    padding: 8px;
  }

  // Ripple feedback is identical across every solid/bordered type — set once here
  // instead of `[&:not(.disabled)_.rf-button--ripple]:bg-black/20` in each of them.
  &--main,
  &--secondary,
  &--tertiary,
  &--quaternary,
  &--ghost {
    &:not(.disabled) .rf-button--ripple {
      background-color: rgb(0 0 0 / 0.2);
    }
  }

  &--link {
    min-height: 44px;
  }
  &--link-sm {
    min-height: 36px;
    padding: 8px 16px;
  }
  &--xxsm {
    min-height: 28px;
    padding: 0 8px;
  }
  &--xsm {
    min-height: 36px;
    padding: 4px 16px 4px 8px;
  }
  &--sm {
    min-height: 36px;
    padding: 8px 16px;
  }

  &--base {
    min-height: 40px;
    padding: 7px 16px;
  }

  &--lg {
    min-height: 44px;
    padding: 8px 32px;
  }

  &--xlg {
    min-height: 50px;
    padding: 8px 32px;
  }

  &--main {
    transition: filter 0.15s ease-in-out;
    &:not(.disabled) {
      &:hover,
      &:focus-visible {
        filter: brightness(105%) saturate(87%);
      }
    }
  }

  &--secondary {
    transition: filter 0.15s ease-in-out;
    &:not(.disabled):not(:active) {
      &:hover,
      &:focus-visible {
        filter: brightness(1.17) saturate(1.14);
      }
    }
  }
}
</style>
